import ct from 'countries-and-timezones';
import { calculateAvailabilityAndLeadTime } from './helpers/availability-lead-time';

function isSupportedTimeZone(timezone) {
  try {
    Intl.DateTimeFormat('en', { timeZone: timezone }).format(0);

    return true;
  } catch {
    return false;
  }
}

class ProductShippingMethod extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.containerElement = this.querySelector('.product-shipping-rates');
    this.shippingRateData = JSON.parse(this.containerElement.dataset.options);
    this.shippingToWrapper = this.querySelector('[data-shipping-to]');
    this.shipmentIsFreeWrapper = this.querySelector('[data-shipment-is-free]');
    this.expectedDeliveryIn = document.querySelector('[data-expected-delivery-in]');

    this.renderExpectedDeliveryIn(1);
    this.showShippingPrice(1);

    this.containerElement.classList.remove('hidden');

    this.quantityInput = document.querySelector('product-form .quantity input');

    this.quantityInput.addEventListener('change', this.onQuantityInputChange.bind(this), {
      signal: this.abortController.signal,
    });

    document
      .getElementById('cart-icon-bubble')
      .addEventListener('cart-update', this.onCartUpdated.bind(this), { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /**
   * Handle cart updated events
   * @param {CustomEvent} event
   * @returns
   */
  onCartUpdated(event) {
    const { cartTotalExclVAT, productQuantity } = event.detail;

    this.shippingRateData.cartValue = cartTotalExclVAT;
    this.shippingRateData.quantityAlreadyInCart = productQuantity;

    const quantityInputValue = this.quantityInput.value;

    this.renderExpectedDeliveryIn(quantityInputValue);
    this.showShippingPrice(quantityInputValue);
  }

  onQuantityInputChange(event) {
    const currentInputValue = parseInt(event.target.value, 10);

    this.renderExpectedDeliveryIn(currentInputValue);
    this.showShippingPrice(currentInputValue);
  }

  // Returns the cutoff hour and timezone abbreviation in the selected country's first supported timezone.
  // Converts the NL cutoff hour to UTC (accounting for CET/CEST DST),
  // then re-formats in that timezone, falling back to the NL timezone if needed.
  getLocalCutoffInfo() {
    const cutoffHour = this.shippingRateData.cutoffHour;
    const nlTimezone = 'Europe/Amsterdam';
    const countryData = ct.getCountry(this.shippingRateData.countryCode);
    const supportedTimezones = (countryData?.timezones ?? []).filter(isSupportedTimeZone);
    const targetTimezone = supportedTimezones[0] ?? nlTimezone;

    // Get today's date as seen in NL timezone (en-CA gives "YYYY-MM-DD" format)
    const now = new Date();
    const nlDateStr = new Intl.DateTimeFormat('en-CA', { timeZone: nlTimezone }).format(now);
    const [year, month, day] = nlDateStr.split('-').map(Number);

    // Find NL's current UTC offset by checking what NL hour noon-UTC maps to.
    // Using noon avoids any DST-transition edge cases at midnight.
    const noonUTC = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const nlHourAtNoon =
      parseInt(
        new Intl.DateTimeFormat('en', { timeZone: nlTimezone, hour: '2-digit', hour12: false })
          .formatToParts(noonUTC)
          .find((p) => p.type === 'hour').value
      ) % 24;
    const nlUTCOffset = nlHourAtNoon - 12; // +1 in winter (CET), +2 in summer (CEST)

    // Build the UTC moment that equals cutoffHour:00 in NL time
    const cutoffUTCHour = cutoffHour - nlUTCOffset;
    const cutoffDate = new Date(Date.UTC(year, month - 1, day, cutoffUTCHour, 0, 0));

    const parts = new Intl.DateTimeFormat('en', {
      timeZone: targetTimezone,
      hour: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).formatToParts(cutoffDate);

    const hour = parseInt(parts.find((p) => p.type === 'hour').value) % 24;
    const timezone = parts.find((p) => p.type === 'timeZoneName').value;

    return { hour, timezone };
  }

  isSkipCutoffCountry() {
    const skipList = this.shippingRateData.cutoffSkipCountries
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    return skipList.includes(this.shippingRateData.countryCode);
  }

  renderExpectedDeliveryIn(currentInputValue) {
    const { availabilityStatus, leadTime } = calculateAvailabilityAndLeadTime(
      this.shippingRateData.leadTimeArray,
      this.shippingRateData.inventoryQuantity,
      this.shippingRateData.inventoryPolicy,
      currentInputValue + this.shippingRateData.quantityAlreadyInCart,
      this.shippingRateData.useup
    );

    const totalMinDays = this.shippingRateData.mintransitTime + leadTime;
    const totalMaxDays = this.shippingRateData.maxtransitTime + leadTime;

    let deliveryText;
    if (availabilityStatus === 'unavailable' || availabilityStatus === 'request') {
      deliveryText = '';
    } else {
      const { hour: localCutoff, timezone: localTimezone } = this.getLocalCutoffInfo();
      // Only show the cutoff prefix when: delivery is fast, country not manually skipped,
      // and the local cutoff time falls within a sensible window (avoids showing e.g. "Order before 1h")
      const showCutoff =
        totalMaxDays <= 4 && !this.isSkipCutoffCountry() && localCutoff >= 8 && localCutoff <= 22;

      const countryData = ct.getCountry(this.shippingRateData.countryCode);
      const offsets = (countryData?.timezones ?? [])
        .filter(isSupportedTimeZone)
        .map((tz) => ct.getTimezone(tz)?.utcOffset);
      const hasMultipleTimezones = new Set(offsets).size > 1;
      const applyTimezone = (str) =>
        str.replace('{{ timezone }}', hasMultipleTimezones && localTimezone ? ` (${localTimezone})` : '');

      if (showCutoff) {
        if (totalMinDays !== totalMaxDays) {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryIn
              .replace('{{ cutoff_time }}', localCutoff)
              .replace('{{ min_transit_time }}', totalMinDays)
              .replace('{{ max_transit_time }}', totalMaxDays)
          );
        } else if (totalMaxDays === 1) {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryInOne.replace('{{ cutoff_time }}', localCutoff)
          );
        } else {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryInDay
              .replace('{{ cutoff_time }}', localCutoff)
              .replace('{{ min_transit_time }}', totalMinDays)
          );
        }
      } else if (this.shippingRateData.mintransitTime !== this.shippingRateData.maxtransitTime) {
        deliveryText = this.shippingRateData.expectedDeliveryIn
          .replace('{{ min_transit_time }}', totalMinDays)
          .replace('{{ max_transit_time }}', totalMaxDays);
      } else if (leadTime === 0) {
        deliveryText = this.shippingRateData.expectedDeliveryInOne;
      } else {
        deliveryText = this.shippingRateData.expectedDeliveryInDay.replace(
          '{{ min_transit_time }}',
          totalMinDays
        );
      }
    }

    if (this.expectedDeliveryIn) {
      this.expectedDeliveryIn.innerHTML = deliveryText;
    }
  }

  showShippingPrice(currentInputValue) {
    const { cartValue, productPrice, freeShipment } = this.shippingRateData;
    const newCartPrice = cartValue + currentInputValue * productPrice;
    const freeShipmentReached = freeShipment && newCartPrice > freeShipment;

    // If free shipment is reached, let's hide shipping cost
    this.shippingToWrapper.hidden = freeShipmentReached;
    // And replace with a text to indicate free shipping is reached
    this.shipmentIsFreeWrapper.hidden = !freeShipmentReached;
  }
}

if (!customElements.get('product-shipping-method')) {
  customElements.define('product-shipping-method', ProductShippingMethod);
}
